import { useState, type RefObject } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { FpPropDefinition, FpPropOption } from "./fpPropSchema";
import { MarginInput } from "./MarginInput";
import { StyleTextInput } from "./StyleTextInput";
import { toHexColor } from "./styleColor";
import { TextColorPicker } from "./TextColorPicker";
import { useRenderedStyle } from "./useRenderedStyle";

/** Sentinel Select value standing for "unset" (Radix forbids ""). */
const UNSET_VALUE = "__unset";

interface FpPropFieldProps {
  /** The prop the field edits. */
  prop: FpPropDefinition;
  /** The block's authored value for the prop ("" when unset). */
  value: string;
  /** Writes (or clears, on "") the attribute on the selected block. */
  onChange: (attribute: string, value: string) => void;
  /** The selected block's node id, which makes control ids unique. */
  nodeId: string;
  /** The preview container, measured for the colour the block renders in. */
  previewRef: RefObject<HTMLDivElement>;
}

/**
 * One generated control from the fp prop schema. The schema names a control
 * type and, for a select, its allowed values; this is the switch that turns
 * that into the shared knobs, so a new primitive needs no new component. A prop
 * the switch cannot express opts out through the schema's override map.
 * @param prop - The prop the field edits
 * @param value - The block's authored value
 * @param onChange - Writes or clears the attribute
 * @param nodeId - The selected block's node id
 * @param previewRef - The preview container, measured for rendered colours
 * @returns The control for the prop
 */
export function FpPropField({
  prop,
  value,
  onChange,
  nodeId,
  previewRef,
}: FpPropFieldProps) {
  if (prop.control === "toggle") {
    const checkedValue = prop.checkedValue ?? "true";
    const controlId = `fp-prop-${nodeId}-${prop.name}`;
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={controlId}
          checked={value === checkedValue}
          onCheckedChange={(state) =>
            onChange(prop.name, state === true ? checkedValue : "")
          }
        />
        <Label htmlFor={controlId} className="text-[11px] font-normal">
          {prop.label}
        </Label>
      </div>
    );
  }

  if (prop.control === "margin") {
    return (
      <MarginInput
        label={prop.label}
        value={value}
        placeholder={prop.placeholder ?? prop.default ?? undefined}
        onCommit={(next) => onChange(prop.name, next)}
      />
    );
  }

  if (prop.control === "color") {
    return (
      <ColorPropField
        prop={prop}
        value={value}
        onChange={onChange}
        nodeId={nodeId}
        previewRef={previewRef}
      />
    );
  }

  if (prop.control === "select") {
    return (
      <div>
        <Label className="mb-1 block text-[11px]">{prop.label}</Label>
        <Select
          value={value === "" ? UNSET_VALUE : value}
          onValueChange={(next) =>
            onChange(prop.name, next === UNSET_VALUE ? "" : next)
          }
        >
          <SelectTrigger className="h-8 w-full text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNSET_VALUE}>{unsetLabel(prop)}</SelectItem>
            {optionsIncluding(prop, value).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <StyleTextInput
      label={prop.label}
      value={value}
      placeholder={prop.placeholder ?? prop.default ?? undefined}
      unitless={prop.control === "text"}
      onCommit={(next) => onChange(prop.name, next)}
    />
  );
}

/**
 * A colour prop's control: a labelled swatch fronting the same palette popover
 * the toolbar's text-colour control uses, in block mode. The swatch shows the
 * colour the block actually renders in - its authored value, or the measured
 * one when the colour comes from elsewhere, like a table stripe painting its
 * header row. Applying writes the attribute; "Default" clears it back.
 * @param prop - The prop the field edits
 * @param value - The block's authored value
 * @param onChange - Writes or clears the attribute
 * @param nodeId - The selected block's node id
 * @param previewRef - The preview container, measured for the rendered colour
 * @returns The colour control
 */
function ColorPropField({
  prop,
  value,
  onChange,
  nodeId,
  previewRef,
}: FpPropFieldProps) {
  const [open, setOpen] = useState(false);
  const measured = useRenderedStyle(
    previewRef,
    nodeId,
    prop.name === "background" ? "background-color" : prop.name,
    value,
  );
  // Fully transparent means nothing actually paints there, so it reads as unset.
  const rendered = measured.endsWith(", 0)") ? "" : measured;
  const effective =
    value !== "" ? value : rendered === "" ? "" : toHexColor(rendered);
  return (
    <div>
      <Label className="mb-1 block text-[11px]">{prop.label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          type="button"
          className="flex h-8 w-full items-center gap-2 rounded-md border border-input bg-transparent px-2 text-xs transition-colors hover:bg-muted data-[state=open]:bg-muted"
        >
          <span
            className={cn(
              "size-4 shrink-0 rounded border border-border",
              effective === "" && "bg-muted",
            )}
            style={
              effective === "" ? undefined : { backgroundColor: effective }
            }
          />
          <span className={cn(value === "" && "text-muted-foreground")}>
            {effective === "" ? unsetLabel(prop) : effective}
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2">
          <TextColorPicker
            editor={null}
            value={effective}
            onApply={(color) => {
              setOpen(false);
              onChange(prop.name, color ?? "");
            }}
            onPreview={(color) => onChange(prop.name, color)}
            onCommitSession={() => {}}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * The label for the "unset" option: what the renderer falls back to, so the
 * panel says what leaving a prop blank actually produces.
 * @param prop - The prop the field edits
 * @returns The option label
 */
function unsetLabel(prop: FpPropDefinition): string {
  return prop.default === null ? "Not set" : `Default (${prop.default})`;
}

/**
 * The pickable options plus the block's own value when it sits outside the
 * vocabulary - a raw hex colour, or a value authored before the vocabulary
 * changed - so an unrecognized value stays visible rather than reading as unset.
 * @param prop - The prop the field edits
 * @param value - The block's authored value
 * @returns The options to render
 */
function optionsIncluding(
  prop: FpPropDefinition,
  value: string,
): readonly FpPropOption[] {
  if (value === "" || prop.options.some((option) => option.value === value)) {
    return prop.options;
  }
  return [...prop.options, { value, label: value }];
}
